import { memo, useContext } from "react";
import PropTypes from "prop-types";
import BackgroundProgressiveImg from "app/pages/.shared/BackgroundProgressiveImg";

import "./SmartDPMapItem.scss";
import ProductNameWithCategory from "app/pages/.shared/ProductNameWithCategory/ProductNameWithCategory";
import classNames from "classnames";
import { Amount } from "app/components/ui/Amount/Amount";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import { FormattedMessage, FormattedPlural } from "react-intl";
import IconLock from "app/pages/.shared/IconLock";
import ProductRatingContainer from "app/pages/FicheProduit/components/ProductRating/ProductRatingContainer";
import { parse } from "qs";
import { useLocation } from "react-router-dom";
import {
	getBoardsDiscountPercentage,
	getBoardsDiscountValue,
	getDiscountBadgeVisibility,
} from "app/utils/discountUtils";
import { PRODUCT_TAG_CODES } from "app/constants";
import AppGlobalsContext from "app/AppGlobalsContext";
import { BRANDS } from "app/constants";

const IMG_SIZES = {
	small: { width: 87, height: 88 },
	medium: { width: 87, height: 88 },
};

const CLOUDINARY_OPTIONS = ["c_fill"];

const SmartDPMapItem = ({ accommodation, icon, tag }) => {
	const { search } = useLocation();
	const { brand } = useContext(AppGlobalsContext);
	const isBrandAF = brand === BRANDS.AF;
	const isBrandTO = brand === BRANDS.TO;
	const isBrandEK = brand === BRANDS.EK;
	const queryParams = parse(search, { ignoreQueryPrefix: true });
	const showDiscountedAccommodation = getDiscountBadgeVisibility(accommodation);
	const discountValue = getBoardsDiscountValue(accommodation);
	const discountedPercentage = getBoardsDiscountPercentage(accommodation, discountValue);
	const smartDPMapItemClasses = classNames({
		"smartDPMap-item": true,
		"smartDPMap-item--has-icon": !!icon,
	});
	const smartDPMapItemIconClasses = classNames({
		"smartDPMap-item__icon": true,
		"smartDPMap-item__icon--GC_DISCONNECTED": tag === PRODUCT_TAG_CODES.GC_DISCONNECTED,
		"smartDPMap-item__icon--GC_DISCONNECTED-af":
			tag === PRODUCT_TAG_CODES.GC_DISCONNECTED && isBrandAF,
		"smartDPMap-item__icon--GC_DISCONNECTED-to":
			tag === PRODUCT_TAG_CODES.GC_DISCONNECTED && isBrandTO,
		"smartDPMap-item__icon--GC_DISCONNECTED-ek":
			tag === PRODUCT_TAG_CODES.GC_DISCONNECTED && isBrandEK,
		"smartDPMap-item__icon--LIMITED_TIME": tag === PRODUCT_TAG_CODES.LIMITED_TIME,
		"smartDPMap-item__icon--LAST_DAY": tag === PRODUCT_TAG_CODES.LAST_DAY,
		"smartDPMap-item__icon--CDC": tag === PRODUCT_TAG_CODES.COUP_DE_COEUR,
		"smartDPMap-item__icon--DISCOUNT": tag === PRODUCT_TAG_CODES.DISCOUNT,
		"smartDPMap-item__icon--BAGGAGE_INCLUDED": tag === PRODUCT_TAG_CODES.BAGGAGE_INCLUDED,
	});

	const hasFlashsaleProductUrl = accommodation.productUrl;
	const tripAdvisor = accommodation.tripadvisor || {};
	const holidayCheck = accommodation.holidaycheck || {};
	const accommodationNights = accommodation?.accommodationNights || queryParams?.duration;

	return (
		<div className={smartDPMapItemClasses}>
			<div className="smartDPMap-item__block smartDPMap-item__block__text">
				<div className="smartDPMap-item__picture">
					<BackgroundProgressiveImg
						sizes={IMG_SIZES}
						cloudinaryOptions={CLOUDINARY_OPTIONS}
						src={
							accommodation.photos &&
							accommodation.photos[0] &&
							accommodation.photos[0].url
						}
					/>
					{icon && <div className={smartDPMapItemIconClasses}>{icon}</div>}
					{hasFlashsaleProductUrl && (
						<div className="smartDPMap-item__lock">
							<IconLock />
						</div>
					)}
				</div>
				<div className="smartDPMap-item__info">
					<div className="smartDPMap-item__label">
						<ProductNameWithCategory
							category={accommodation.category}
							name={accommodation.name}
						/>
					</div>

					<ProductRatingContainer
						locationName={accommodation.name}
						tripAdvisor={{
							id: tripAdvisor.id,
							rating: tripAdvisor.rating,
							reviewsCount: tripAdvisor.num_reviews,
							hideIcon: true,
						}}
						holidayCheck={{
							...holidayCheck,
							size: "small",
						}}
					/>
					<div className="smartDPMap-item__price-cta" data-testid="smartDPMap-item-price">
						{accommodation.total && (
							<Amount
								value={accommodation.total}
								perPerson
								size="large"
								suffixSize="xsmall"
							/>
						)}
						{showDiscountedAccommodation && discountedPercentage !== 0 && (
							<Typography
								className="smartDPMap-item__discount-percentage"
								variant={TYPOGRAPHY_VARIANTS.REGULAR}
								data-testid="smartDPMap-item-discount-percentage"
							>
								-{discountedPercentage}%
							</Typography>
						)}
					</div>

					{accommodationNights && accommodationNights > 0 && (
						<div
							className="smartDPMap-item__hotel-nights"
							data-testid="smartDPMap-item-hotel-nights"
						>
							<FormattedPlural
								data-testid="night-count"
								value={accommodationNights}
								one={
									<FormattedMessage
										id="general.duration.singular"
										values={{ duration: accommodationNights }}
									>
										{accommodationNights}
									</FormattedMessage>
								}
								other={
									<FormattedMessage
										id="general.duration.plural"
										values={{ duration: accommodationNights }}
									>
										{accommodationNights}
									</FormattedMessage>
								}
							/>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

SmartDPMapItem.propTypes = {
	accommodation: PropTypes.object,
	icon: PropTypes.element,
	tag: PropTypes.oneOf(Object.values(PRODUCT_TAG_CODES)),
};

export default memo(SmartDPMapItem);
